import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["url", "title", "description", "imageUrl", "previewContainer", "previewImage", "previewTitle", "previewDescription", "loadingIndicator"]
  static values = { proxyUrl: String }

  connect() {
    // Debounce timer
    this.timeout = null
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  fetchPreview() {
    // Clear existing timeout
    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    const url = this.urlTarget.value.trim()

    // Clear preview if URL is empty
    if (!url) {
      this.clearPreview()
      return
    }

    // Validate URL format
    if (!this.isValidUrl(url)) {
      this.clearPreview()
      return
    }

    // Debounce: wait 500ms after user stops typing
    this.timeout = setTimeout(() => {
      this.fetchPreviewData(url)
    }, 500)
  }

  async fetchPreviewData(url) {
    // Show loading state
    this.showLoading()

    try {
      // Use proxy endpoint to avoid CORS issues
      const proxyUrl = this.proxyUrlValue || '/link_preview/fetch'
      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({ url })
      })

      if (!response.ok) {
        throw new Error('Failed to fetch preview')
      }

      const data = await response.json()

      // Update form fields with fetched data
      if (data.title) {
        this.titleTarget.value = data.title
      }
      if (data.description) {
        this.descriptionTarget.value = data.description
      }
      if (data.image_url) {
        this.imageUrlTarget.value = data.image_url
      }

      // Update preview display
      this.showPreview(data)

    } catch (error) {
      console.error('Error fetching link preview:', error)
      this.clearPreview()
    } finally {
      this.hideLoading()
    }
  }

  showLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add('hidden')
    }
  }

  showPreview(data) {
    if (!this.hasPreviewContainerTarget) return

    this.previewContainerTarget.classList.remove('hidden')

    if (this.hasPreviewImageTarget && data.image_url) {
      this.previewImageTarget.src = data.image_url
      this.previewImageTarget.classList.remove('hidden')
    }

    if (this.hasPreviewTitleTarget && data.title) {
      this.previewTitleTarget.textContent = data.title
      this.previewTitleTarget.classList.remove('hidden')
    }

    if (this.hasPreviewDescriptionTarget && data.description) {
      this.previewDescriptionTarget.textContent = data.description
      this.previewDescriptionTarget.classList.remove('hidden')
    }
  }

  clearPreview() {
    // Clear form fields
    if (this.hasTitleTarget) {
      this.titleTarget.value = ''
    }
    if (this.hasDescriptionTarget) {
      this.descriptionTarget.value = ''
    }
    if (this.hasImageUrlTarget) {
      this.imageUrlTarget.value = ''
    }

    // Hide preview
    if (this.hasPreviewContainerTarget) {
      this.previewContainerTarget.classList.add('hidden')
    }
    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.classList.add('hidden')
    }
    if (this.hasPreviewTitleTarget) {
      this.previewTitleTarget.classList.add('hidden')
    }
    if (this.hasPreviewDescriptionTarget) {
      this.previewDescriptionTarget.classList.add('hidden')
    }
  }

  isValidUrl(string) {
    try {
      const url = new URL(string)
      return url.protocol === 'http:' || url.protocol === 'https:'
    } catch (_) {
      return false
    }
  }

  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ''
  }
}
